import React, { useEffect, useMemo, useState } from "react";
import api from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import { useTable, useFilters } from "react-table";
import Draggable from "react-draggable";

import {
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLazyAccordion from "pages/components/DashboardLazyAccordion";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function DefaultColumnFilter({ column }) {
  const { filterValue, setFilter } = column;
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "2px 4px",
        fontSize: 11,
        border: "none",
        borderBottom: "1px solid #e0e0e0",
        outline: "none",
        backgroundColor: "transparent",
      }}
      placeholder="Filtrar."
    />
  );
}

function metricLabel(k) {
  if (k === "qtd") return "qtd";
  if (k === "euros") return "euros";
  if (k === "litros") return "litros";
  if (k === "clientes") return "clientes";
  return k;
}

function toISO(d) {
  if (!d) return "";

  const value = String(d).trim();
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);

  return match ? match[1] : value;
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function VendedoresMiniTable({ vendedores = [], tipoKey = "qtd" }) {
  const rows = Array.isArray(vendedores) ? vendedores : [];

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fmt = (n) => {
    const v = safeNum(n);
    // mostra 0 sem casas; se quiseres sempre 2 casas, muda para toFixed(2)
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  };

  const calc = rows
    .map((v) => {
      const met = v?.[tipoKey] || {};
      const objetivo = safeNum(met.objetivo);
      const real = safeNum(met.vendas);
      if (objetivo <= 0) return null; // só mostra vendedores com objetivo definido
      const dif = real - objetivo;
      const perc = objetivo > 0 ? (real / objetivo) * 100 : null;

      return {
        key: v?.refvendedor || v?.vendedor || Math.random(),
        vendedor: v?.vendedor || v?.refvendedor || "",
        objetivo,
        real,
        dif,
        perc,
      };
    })
    .filter(Boolean);

  const totalObj = calc.reduce((a, r) => a + safeNum(r.objetivo), 0);
  const totalReal = calc.reduce((a, r) => a + safeNum(r.real), 0);
  const totalDif = totalReal - totalObj;
  const totalPerc = totalObj > 0 ? (totalReal / totalObj) * 100 : null;

  return (
    <div>
      <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 8 }}>
        Individuais — detalhe por vendedor
      </div>

      <div style={{ maxHeight: 260, overflow: "auto", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={thMini}>Vendedor</th>
              <th style={thMini}>Objetivo</th>
              <th style={thMini}>Real</th>
              <th style={thMini}>Dif.</th>
              <th style={thMini}>%</th>
            </tr>
          </thead>

          <tbody>
            {calc.map((r) => (
              <tr key={r.key} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={tdMini}>{r.vendedor}</td>
                <td style={tdMini}>{fmt(r.objetivo)}</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(r.real)}</td>
                <td style={{ ...tdMini, color: r.dif >= 0 ? "#4caf50" : "#d32f2f" }}>
                  {r.dif.toFixed(2)}
                </td>
                <td style={tdMini}>{r.perc != null ? `${r.perc.toFixed(1)}%` : ""}</td>
              </tr>
            ))}

            {calc.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...tdMini, textAlign: "center", color: "#9e9e9e" }}>
                  Sem dados (nenhum vendedor com objetivo definido).
                </td>
              </tr>
            )}
          </tbody>

          {calc.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...tdMini, fontWeight: 700 }}>Total</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(totalObj)}</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(totalReal)}</td>
                <td
                  style={{
                    ...tdMini,
                    fontWeight: 700,
                    color: totalDif >= 0 ? "#4caf50" : "#d32f2f",
                  }}
                >
                  {totalDif.toFixed(2)}
                </td>
                <td style={{ ...tdMini, fontWeight: 700 }}>
                  {totalPerc != null ? `${totalPerc.toFixed(1)}%` : ""}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function numFmt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function difCellMini(objetivo, real) {
  const o = safeNum(objetivo);
  const r = safeNum(real);
  if (!o) return <span>-</span>;

  const dif = r - o;
  const color = dif > 0 ? "#4caf50" : dif < 0 ? "#d32f2f" : undefined;

  return <span style={{ color, fontWeight: 500 }}>{numFmt(dif)}</span>;
}

function pctFmtMini(objetivo, real) {
  const o = safeNum(objetivo);
  const r = safeNum(real);
  if (!o) return "-";

  const pct = (r / o) * 100;
  return `${pct.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
}

const thMini = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "#fff",
  textAlign: "left",
  padding: "6px 8px",
  fontSize: 12,
  color: "#7b809a",
  verticalAlign: "bottom",
  boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
};

const tdMini = {
  padding: "6px 8px",
  whiteSpace: "nowrap",
};

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

function ObjetivosGrupoTable({ columns, data }) {
  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = tableInstance;

  return (
    <MDBox
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        maxHeight: 360,
        overflowY: "auto",
        borderRadius: 1,
        "& tbody tr:hover": {
          backgroundColor: "rgba(0,0,0,0.02)",
        },
      }}
    >
      <table
        {...getTableProps()}
        style={{
          width: "100%",
          minWidth: 900,
          borderCollapse: "collapse",
          fontSize: 12,
        }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()} key={hg.id}>
              {hg.headers.map((col) => (
                <th
                  {...col.getHeaderProps()}
                  key={col.id}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: "#fff",
                    textAlign: "left",
                    padding: "6px 8px",
                    fontSize: 12,
                    color: "#7b809a",
                    verticalAlign: "bottom",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                  }}
                >
                  <div>{col.render("Header")}</div>

                  {!col.disableFilters && (
                    <div onClick={(event) => event.stopPropagation()} style={{ marginTop: 4 }}>
                      {col.canFilter ? col.render("Filter") : null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {tableRows.map((r) => {
            prepareRow(r);

            return (
              <tr
                {...r.getRowProps()}
                key={r.id}
                style={{
                  borderBottom: "1px solid #f0f0f0",
                  transition: "background-color 0.15s",
                }}
              >
                {r.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    key={cell.column.id}
                    style={{
                      padding: "6px 8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </MDBox>
  );
}

export default function ObjetivosEstadoDashboard({
  autoLoad = true,
  refreshToken = 0,
  onLoadingChange,
  onLoadedChange,
}) {
  const { userData, temFuncao } = useAuth();

  // ⚠️ Ajusta aqui para bater com o teu "isAdminLike" do Objetivos.js
  const isAdminLike =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Gestão Objetivos");

  const refUser = userData?.refvendedor ? String(userData.refvendedor) : "";
  const isVendedor = !!refUser;

  // isAdminLike aqui é só gate (poder ver o componente)
  const canSee = isAdminLike || isVendedor;

  const isComercialRestrito = !isAdminLike && isVendedor;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [carregado, setCarregado] = useState(false);
  const [erro, setErro] = useState("");

  const [openInfo, setOpenInfo] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoRow, setInfoRow] = useState(null);
  const [infoDetalhes, setInfoDetalhes] = useState(null);
  const [infoEqRows, setInfoEqRows] = useState([]); // opcional: só usado para Equipa

  const abrirInfo = async (row) => {
    if (!row?.idobjetivo) return;

    setInfoRow(row);
    setInfoDetalhes(null);
    setInfoEqRows([]);
    setOpenInfo(true);

    setInfoLoading(true);
    try {
      const detRes = await api.post("Comercial/SelObjetivoDetalhesCache", { id: row.idobjetivo });
      const det = detRes.data?.data ?? detRes.data;

      setInfoDetalhes(det);
    } catch {
      setInfoDetalhes(null);
    } finally {
      setInfoLoading(false);
    }
  };

  async function loadFromCache() {
    if (!canSee) {
      onLoadingChange?.(false);
      return;
    }

    setLoading(true);
    onLoadingChange?.(true);
    setErro("");

    try {
      // 1) lista objetivos a partir da cache
      const bodyObj = isComercialRestrito
        ? { refvendedor: refUser, apenasVigentes: true }
        : { apenasVigentes: true };

      const resObj = await api.post("Comercial/SelObjetivosCache", bodyObj);
      const ativos = Array.isArray(resObj.data) ? resObj.data : resObj.data?.data || [];

      const detalhesAll = await Promise.all(
        ativos.map(async (o) => {
          try {
            const detRes = await api.post("Comercial/SelObjetivoDetalhesCache", { id: o.id });
            const det = detRes.data?.data ?? detRes.data;

            return { obj: o, det };
          } catch (err) {
            console.log("Falha SelObjetivoDetalhesCache", o?.id, err);
            return { obj: o, det: null };
          }
        })
      );

      // console.log("detalhesAll", detalhesAll);

      const out = [];

      for (const it of detalhesAll) {
        const o = it.obj;
        const det = it.det;

        if (!det) {
          console.log("Objetivo sem detalhe em cache", o);
          continue;
        }

        const listIndVend = Array.isArray(det?.individuais_vendedores)
          ? det.individuais_vendedores
          : [];

        const nomeObjetivo = o?.objetivo_nome || o?.nome || `Objetivo #${o?.id || ""}`;

        const metricas = ["qtd", "euros", "litros", "clientes"];

        for (const met of metricas) {
          // ============================
          // 🧑‍💼 ADMIN → LINHA AGREGADA
          // ============================
          if (isAdminLike) {
            let totalObj = 0;
            let totalReal = 0;

            for (const v of listIndVend) {
              const dados = v?.[met];
              totalObj += safeNum(dados?.objetivo);
              totalReal += safeNum(dados?.vendas);
            }

            if (totalObj <= 0) continue;

            const dif = totalReal - totalObj;
            const perc = totalObj > 0 ? (totalReal / totalObj) * 100 : 0;

            out.push({
              idobjetivo: o.id,
              idobjetivogrupo: o.idobjetivogrupo ?? null,
              grupo_nome: String(o.grupo_nome || "Sem grupo").trim() || "Sem grupo",
              metrica: met,
              tipo: "Vendedor",
              vendedor: "", // vazio (não faz sentido aqui)
              nome: `${nomeObjetivo} (${metricLabel(met)})`,
              inicio: toISO(o.dataini),
              fim: toISO(o.datafim),
              objetivo: totalObj,
              real: totalReal,
              dif,
              perc,
            });

            continue;
          }

          // ============================
          // 👤 UTILIZADOR → SÓ O SEU
          // ============================
          const minhaLinha = listIndVend.find((v) => String(v?.refvendedor) === String(refUser));

          if (!minhaLinha?.[met]) continue;

          const dados = minhaLinha[met];
          const objetivo = safeNum(dados?.objetivo);

          if (objetivo <= 0) continue;

          const real = safeNum(dados?.vendas);
          const dif = real - objetivo;
          const perc = objetivo > 0 ? (real / objetivo) * 100 : 0;

          out.push({
            idobjetivo: o.id,
            idobjetivogrupo: o.idobjetivogrupo ?? null,
            grupo_nome: String(o.grupo_nome || "Sem grupo").trim() || "Sem grupo",
            metrica: met,
            tipo: "Vendedor",
            vendedor: minhaLinha?.vendedor || "",
            nome: `${nomeObjetivo} (${metricLabel(met)})`,
            inicio: toISO(o.dataini),
            fim: toISO(o.datafim),
            objetivo,
            real,
            dif,
            perc,
          });
        }
      }

      // console.log("Objetivos dashboard out", out);
      // console.log("Objetivos dashboard out total", out.length);

      setRows(out);
      setCarregado(true);
      onLoadedChange?.(true);
    } catch (e) {
      setErro("Não foi possível carregar os objetivos.");
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }

  useEffect(() => {
    if (!autoLoad && refreshToken <= 0) return;
    if (!canSee) return;

    loadFromCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, refreshToken, canSee, userData?.id, userData?.refvendedor]);

  const columns = useMemo(() => {
    const cols = [
      {
        Header: "",
        id: "info",
        disableFilters: true,
        Cell: ({ row }) => {
          const r = row.original;

          if (!isAdminLike) return null;

          return (
            <Tooltip title="Ver detalhe por vendedor" arrow placement="top">
              <IconButton size="small" color="info" onClick={() => abrirInfo(r)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      { Header: "Tipo", accessor: "tipo", Filter: DefaultColumnFilter },
      { Header: "Nome", accessor: "nome", Filter: DefaultColumnFilter },

      {
        Header: "%",
        accessor: "perc",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => {
          const valor = Number(value);

          if (!Number.isFinite(valor)) return "";

          const percentagemVisual = Math.max(0, Math.min(100, valor));

          return (
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 150,
              }}
            >
              <MDTypography
                variant="caption"
                sx={{
                  minWidth: 42,
                  textAlign: "right",
                  fontSize: 12,
                  color: "#344767",
                }}
              >
                {valor.toFixed(1)}%
              </MDTypography>

              <MDBox
                sx={{
                  position: "relative",
                  width: 92,
                  height: 9,
                  borderRadius: 1,
                  overflow: "hidden",
                  backgroundColor: "rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.10)",
                }}
              >
                <MDBox
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: `${percentagemVisual}%`,
                    height: "100%",
                    overflow: "hidden",
                    transition: "width 0.25s ease",
                  }}
                >
                  <MDBox
                    sx={{
                      width: 92,
                      height: "100%",
                      background:
                        "linear-gradient(90deg, #f44336 0%, #ff5722 25%, #ffeb3b 50%, #8bc34a 75%, #4caf50 100%)",
                    }}
                  />
                </MDBox>
              </MDBox>
            </MDBox>
          );
        },
      },

      {
        Header: "Objetivo",
        accessor: "objetivo",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) =>
          safeNum(value).toLocaleString("pt-PT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      },
      {
        Header: !isAdminLike ? "Real (meu)" : "Real",
        accessor: "real",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => (
          <b>
            {safeNum(value).toLocaleString("pt-PT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </b>
        ),
      },
      {
        Header: "Dif.",
        accessor: "dif",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => {
          const v = safeNum(value);
          const txt = Number.isFinite(v) ? v.toFixed(2) : "";
          return <span style={{ color: v >= 0 ? "#4caf50" : "#d32f2f" }}>{txt}</span>;
        },
      },

      { Header: "Início", accessor: "inicio", Filter: DefaultColumnFilter },
      { Header: "Fim", accessor: "fim", Filter: DefaultColumnFilter },
    ];

    if (!isAdminLike) {
      cols.splice(1, 0, {
        Header: "Vendedor",
        accessor: "vendedor",
        Filter: DefaultColumnFilter,
      });
    }

    return cols;
  }, [isAdminLike]);

  const grupos = useMemo(() => {
    const map = new Map();

    rows.forEach((r) => {
      const id = r.idobjetivogrupo ?? 0;
      const nome = String(r.grupo_nome || "Sem grupo").trim() || "Sem grupo";

      if (!map.has(id)) {
        map.set(id, {
          id,
          nome,
          rows: [],
          idsObjetivos: new Set(),
        });
      }

      const grupo = map.get(id);

      grupo.rows.push(r);

      if (r.idobjetivo != null) {
        grupo.idsObjetivos.add(String(r.idobjetivo));
      }
    });

    return Array.from(map.values())
      .map((grupo) => ({
        id: grupo.id,
        nome: grupo.nome,
        rows: grupo.rows,
        totalObjetivos: grupo.idsObjetivos.size,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-PT"));
  }, [rows]);

  if (!canSee) return null;

  return (
    <>
      <MDBox sx={{ width: "100%", maxWidth: "100%" }}>
        {loading && carregado ? (
          <MDBox py={2} display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </MDBox>
        ) : null}

        {carregado && grupos.length > 0 && (
          <MDBox
            sx={{
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {grupos.map((grupo) => {
              const textoQuantidade =
                grupo.totalObjetivos === 1 ? "1 objetivo" : `${grupo.totalObjetivos} objetivos`;

              return (
                <DashboardLazyAccordion
                  key={`objetivos-grupo-${grupo.id}`}
                  title={`${grupo.nome} (${textoQuantidade})`}
                  loaded
                  showRefreshButton={false}
                >
                  <ObjetivosGrupoTable columns={columns} data={grupo.rows} />
                </DashboardLazyAccordion>
              );
            })}
          </MDBox>
        )}

        {carregado && grupos.length === 0 && !loading && (
          <MDBox py={2}>
            <MDTypography
              variant="button"
              color="text"
              sx={{
                display: "block",
                textAlign: "center",
                fontWeight: "regular",
              }}
            >
              Não foram encontrados objetivos para apresentar.
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      <Dialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
          Detalhe por vendedor
        </DialogTitle>

        <DialogContent dividers>
          {infoLoading && (
            <MDBox display="flex" alignItems="center" gap={1} py={2}>
              <CircularProgress size={20} />
              <span>A carregar…</span>
            </MDBox>
          )}

          {!infoLoading && !infoDetalhes && <div style={{ opacity: 0.8 }}>Sem dados.</div>}

          {!infoLoading && infoDetalhes && (
            <VendedoresMiniTable
              vendedores={infoDetalhes?.individuais_vendedores}
              tipoKey={infoRow?.metrica}
            />
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenInfo(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
